import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { Card } from 'react-bootstrap';
import {
  Edge,
  MiniMap,
  ReactFlow,
  ReactFlowInstance,
  ReactFlowProvider,
  useEdgesState,
  useKeyPress,
  useNodesState,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { Theme } from '../../../../theme';
import { withChildren } from '../../../../utils/react';
import { EmployeeInfoSidebar } from '../../components/EmployeeInfoSidebar';
import { DetailsFilter } from './Actions/DetailsFilter';
import { OrgChartIconButton } from './Actions/OrgChartIconButton';
import EmployeeNode from './EmployeeNode';
import {
  MAX_ZOOM,
  NODE_HEIGHT,
  NODE_WIDTH,
  getChildrenIds,
  getFocusZoom,
  getNodesAndEdges,
} from './helper';
import './index.css';
import OrgChartFilters from './OrgChartFilters';
import { Search } from './Search';
import { EmployeeDetail, OrgChartEmployee, OrgChartNode } from './types';
import { getFindModeEmployees, useOrgChartFilters } from './useOrgChartFilters';

type Props = {
  employees: OrgChartEmployee[];
};

const nodeTypes = {
  employeeNode: EmployeeNode,
};

const FullScreenWithChildren = withChildren(FullScreen);

export const Content: React.FC<React.PropsWithChildren<Props>> = ({
  employees,
}) => {
  const [reactFlowInstance, setReactFlowInstance] =
    useState<ReactFlowInstance<OrgChartNode, Edge>>();
  const { setCenter } = useReactFlow();
  const fullScreenHandle = useFullScreenHandle();
  const [selectedEmployee, setSelectedEmployee] =
    useState<OrgChartEmployee | null>(null);

  const {
    filters,
    setFilters,
    filteredEmployees,
    hasActiveFilters,
    clearFilters,
  } = useOrgChartFilters(employees);

  const [modifiedEmployees, setModifiedEmployees] = useState<
    OrgChartEmployee[]
  >([]);
  const [searchedId, setSearchedId] = useState<string | null>(null);
  const [searchMode, setSearchMode] = useState<string>('focus');
  const [nodes, setNodes, onNodesChange] = useNodesState<OrgChartNode>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [detailsSettings] = useState<EmployeeDetail[]>([
    'name',
    'avatar',
    'position',
    'department',
  ]);

  //Equal is used to handle some keyboards that have a key with two flavor (=, +)
  //Same for Minus
  const plusKeyboardPressed = useKeyPress(['Equal', 'NumpadAdd']);
  const subtractKeyboardPressed = useKeyPress(['Minus', 'NumpadSubtract']);

  const [selectedSettings, setSelectedSettings] =
    useState<string[]>(detailsSettings);

  useEffect(() => {
    const { nodes, edges } = getNodesAndEdges(
      modifiedEmployees,
      selectedSettings,
    );
    setNodes(nodes);
    setEdges(edges);
  }, [
    modifiedEmployees,
    setEdges,
    setNodes,
    selectedSettings,
    reactFlowInstance,
  ]);

  // Refit the viewport when the visible set changes so the compressed layout is
  // on screen without manual scrolling. Only `focus` mode is exempt — it drives
  // its own setCenter. `find` relies on this refit to frame the reporting line.
  //
  // Keyed on the layout inputs rather than on `nodes`: onNodesChange replaces
  // the node array on every selection and measurement event, which would
  // re-animate the viewport on each node click.
  useEffect(() => {
    if (!reactFlowInstance) return;
    if (searchedId !== null && searchMode === 'focus') return;

    const timer = setTimeout(() => {
      reactFlowInstance.fitView({ duration: 400, padding: 0.2 });
    }, 50);
    return () => clearTimeout(timer);
  }, [
    modifiedEmployees,
    selectedSettings,
    reactFlowInstance,
    searchedId,
    searchMode,
  ]);

  // A searched employee missing from the filtered set is hidden by an active
  // filter, so both modes recover the same way: drop the filters and let the
  // next pass find them.
  const searchedIsHidden =
    searchedId !== null && !filteredEmployees.some((e) => e.id === searchedId);

  useEffect(() => {
    if (searchedId !== null && searchMode === 'focus') {
      if (searchedIsHidden) {
        clearFilters();
        return;
      }
      const searchedNode = nodes.find((n) => n.data.employee.id === searchedId);
      if (searchedNode != null) {
        // v12 measures nodes asynchronously; fall back to the layout size until
        // the first measurement lands, otherwise the centre is NaN.
        const width = searchedNode.measured?.width ?? NODE_WIDTH;
        const height = searchedNode.measured?.height ?? NODE_HEIGHT;
        const x = searchedNode.position.x + width / 2;
        const y = searchedNode.position.y + height / 2;
        setCenter(x, y, { zoom: getFocusZoom(MAX_ZOOM), duration: 1000 });
      }
    }
  }, [
    nodes,
    searchMode,
    searchedId,
    setCenter,
    searchedIsHidden,
    clearFilters,
  ]);

  useEffect(() => {
    if (searchedId !== null && searchMode === 'find') {
      if (searchedIsHidden) {
        clearFilters();
        return;
      }
      setModifiedEmployees(getFindModeEmployees(filteredEmployees, searchedId));
    } else {
      setModifiedEmployees(filteredEmployees);
    }
  }, [
    filteredEmployees,
    searchedId,
    searchMode,
    searchedIsHidden,
    clearFilters,
  ]);

  useEffect(() => {
    reactFlowInstance?.zoomIn();
  }, [reactFlowInstance, plusKeyboardPressed]);

  useEffect(() => {
    reactFlowInstance?.zoomOut();
  }, [reactFlowInstance, subtractKeyboardPressed]);

  const collapse = (node: OrgChartNode) => {
    const childrenIds = new Set(getChildrenIds(node, nodes, edges, []));
    setModifiedEmployees((employees) =>
      employees
        .filter((e) => !childrenIds.has(e.id))
        .map((e) => (e.id === node.id ? { ...e, isCollapsed: true } : e)),
    );
  };

  const expand = (node: OrgChartNode) => {
    setModifiedEmployees((employees) => {
      // In find mode an ancestor of the searched employee already has that one
      // child on screen, so only append children that are not visible yet.
      const visibleIds = new Set(employees.map((e) => e.id));
      const children = filteredEmployees
        .filter((e) => e.manager?.id === node.id && !visibleIds.has(e.id))
        .map((e) => ({ ...e, isCollapsed: true }));

      return employees
        .map((e) => (e.id === node.id ? { ...e, isCollapsed: false } : e))
        .concat(children);
    });
  };

  const isClickOnExpandCollapse = (_event: React.MouseEvent) => {
    // @ts-ignore
    const { dataset, tagName } = _event.target;
    return (
      dataset.id === 'no-of-employees-container' ||
      tagName === 'path' ||
      tagName === 'svg'
    );
  };

  const onNodeClick = (_event: React.MouseEvent, node: OrgChartNode) => {
    if (isClickOnExpandCollapse(_event)) {
      if (node.data.employee.isCollapsed) {
        expand(node);
      } else {
        collapse(node);
      }
    } else {
      setSelectedEmployee(node.data.employee);
    }
  };

  const onCloseSidebar = () => setSelectedEmployee(null);

  return (
    <FullScreenWithChildren handle={fullScreenHandle}>
      <Card>
        <Card.Header>
          <div
            className={classNames(
              'd-flex',
              'justify-content-between',
              'align-items-center',
              'small',
            )}>
            <Search
              employees={employees}
              searchedId={searchedId}
              setSearchedId={setSearchedId}
              searchMode={searchMode}
              setSearchMode={setSearchMode}
            />
            <OrgChartFilters
              employees={employees}
              filters={filters}
              onFiltersChange={setFilters}
              hasActiveFilters={hasActiveFilters}
              onClearFilters={clearFilters}
            />
            <div
              className={classNames('d-flex', 'align-items-center', 'gap-4')}>
              <DetailsFilter
                detailsSettings={detailsSettings}
                selectedIds={selectedSettings}
                onSelectedIdsChange={setSelectedSettings}
              />
              <div
                className={classNames('d-flex', 'align-items-center', 'gap-3')}>
                {fullScreenHandle.active ? (
                  <OrgChartIconButton
                    icon="compress-outline"
                    onClick={() => fullScreenHandle.exit()}
                  />
                ) : (
                  <OrgChartIconButton
                    icon="expand-outline"
                    onClick={() => fullScreenHandle.enter()}
                  />
                )}
                <OrgChartIconButton
                  icon="remove-outline"
                  onClick={() => reactFlowInstance?.zoomOut()}
                />
                <OrgChartIconButton
                  icon="add-outline"
                  onClick={() => reactFlowInstance?.zoomIn()}
                />
              </div>
            </div>
          </div>
        </Card.Header>
        <Card.Body
          className="p-0"
          style={{ height: fullScreenHandle.active ? '100vh' : '75vh' }}>
          <ReactFlow
            style={{ cursor: 'grab' }}
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={onNodeClick}
            nodeTypes={nodeTypes}
            nodesDraggable={false}
            maxZoom={MAX_ZOOM}
            onInit={setReactFlowInstance}
            fitView>
            <MiniMap nodeColor={Theme.color.blue3} nodeStrokeWidth={1} />
          </ReactFlow>
        </Card.Body>

        <EmployeeInfoSidebar
          show={!!selectedEmployee}
          onClose={onCloseSidebar}
          employee={selectedEmployee}
        />
      </Card>
    </FullScreenWithChildren>
  );
};

export const OrgChart: React.FC<React.PropsWithChildren<Props>> = (props) => (
  <ReactFlowProvider>
    <Content {...props} />
  </ReactFlowProvider>
);
